import { useState } from 'react'
import { Activity, RefreshCw, AlertTriangle, Search } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Result {
  total_events: number
  events_sample: any[]
  top_src_ips: { ip: string; count: number }[]
  top_dst_ips: { ip: string; count: number }[]
  top_signatures: { sig: string; count: number }[]
  severity_distribution: { severity: number; count: number }[]
  interesting_ports: { port: number; count: number; label: string }[]
}

const SEV_TONE: Record<number, string> = {
  1: 'text-rose',
  2: 'text-amber-light',
  3: 'text-blue-light',
}

const SAMPLE = `{"timestamp":"2026-04-20T10:00:00Z","event_type":"alert","src_ip":"45.61.169.5","src_port":51234,"dest_ip":"10.0.0.5","dest_port":3389,"proto":"TCP","alert":{"signature":"ET SCAN RDP brute force","category":"Attempted Administrator Privilege Gain","severity":2,"signature_id":2024792}}
{"timestamp":"2026-04-20T10:00:01Z","event_type":"alert","src_ip":"45.61.169.5","src_port":51235,"dest_ip":"10.0.0.5","dest_port":3389,"proto":"TCP","alert":{"signature":"ET SCAN RDP brute force","category":"Attempted Administrator Privilege Gain","severity":2,"signature_id":2024792}}
{"timestamp":"2026-04-20T10:00:02Z","event_type":"dns","src_ip":"10.0.0.42","dest_ip":"8.8.8.8","dns":{"rrname":"evil.example.com"}}`

export default function NsmLensPage() {
  const [blob, setBlob] = useState('')
  const [data, setData] = useState<Result | null>(null)
  const [loading, setLoading] = useState(false)

  const analyse = async () => {
    if (!blob.trim()) return
    setLoading(true); setData(null)
    try {
      const r = await api.post('/api/sec/nsm-lens', { blob })
      setData(r.data)
    } finally { setLoading(false) }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
          <Activity size={24} className="text-cyan" /> NSM Log Lens
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Paste Suricata EVE JSON or Zeek conn.log / notice.log lines. Get top talkers, signatures, severity, and interesting-port hits.
        </p>
      </div>

      <div className="card p-4">
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase tracking-wider text-text-muted">Log paste (one event per line)</label>
          <button type="button" onClick={() => setBlob(SAMPLE)} className="text-[10px] text-blue-light hover:underline">
            load sample
          </button>
        </div>
        <textarea value={blob} onChange={e => setBlob(e.target.value)} rows={10}
          placeholder='{"event_type":"alert", ...} or Zeek TSV — one per line'
          className="input w-full py-2 font-mono text-[10px]" />
        <div className="flex justify-end mt-2">
          <button type="button" onClick={analyse} disabled={loading || !blob.trim()} className="btn-primary text-xs">
            {loading ? <RefreshCw size={12} className="animate-spin" /> : <Search size={12} />}
            Analyse {blob ? `(${blob.split('\n').filter(l => l.trim()).length} lines)` : ''}
          </button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Events parsed" value={data.total_events} tone="blue" />
            <Tile label="Unique src IPs" value={data.top_src_ips.length} tone="emerald" />
            <Tile label="Signatures" value={data.top_signatures.length} tone="amber" />
            <Tile label="Hot ports hit" value={data.interesting_ports.length} tone={data.interesting_ports.length > 0 ? 'rose' : 'emerald'} pulse={data.interesting_ports.length > 0} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Section title="Top src IPs" rows={data.top_src_ips.map(x => [x.ip, x.count])} />
            <Section title="Top dst IPs" rows={data.top_dst_ips.map(x => [x.ip, x.count])} />
            <Section title="Top signatures" rows={data.top_signatures.map(x => [x.sig, x.count])} />
            <Section title="Severity distribution" rows={data.severity_distribution.map(x => [`Sev ${x.severity}`, x.count])} />
          </div>

          {data.interesting_ports.length > 0 && (
            <div className="card p-3 border border-rose/30 bg-rose/5">
              <h3 className="text-sm font-semibold text-rose-light mb-2 flex items-center gap-2">
                <AlertTriangle size={14} /> Interesting destination ports
              </h3>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-text-muted">
                    <th className="text-left py-1 px-2">Port</th>
                    <th className="text-left py-1 px-2">Service / risk</th>
                    <th className="text-right py-1 px-2">Hits</th>
                  </tr>
                </thead>
                <tbody>
                  {data.interesting_ports.map(p => (
                    <tr key={p.port} className="border-t border-border/50">
                      <td className="py-1 px-2 font-mono font-bold text-rose-light">{p.port}</td>
                      <td className="py-1 px-2 text-text-secondary">{p.label}</td>
                      <td className="py-1 px-2 text-right font-mono text-text-primary">{p.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Tile({ label, value, tone, pulse }: { label: string; value: number; tone: string; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}

function Section({ title, rows }: { title: string; rows: [string, number][] }) {
  return (
    <div className="card p-3">
      <h3 className="text-xs font-semibold text-text-primary mb-2">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-[10px] text-text-muted italic">No data.</p>
      ) : (
        <table className="w-full text-xs">
          <tbody>
            {rows.slice(0, 8).map(([k, v]) => (
              <tr key={k} className="border-b border-border/30">
                <td className="py-1 px-1 text-text-secondary truncate max-w-[200px]" title={k}>{k}</td>
                <td className="py-1 px-1 text-right font-mono font-bold text-text-primary">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
